import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  IAppSettings,
  // NotInitial,
  fetchAppSettings,
  patchAppSettingsData,
  postAppSettingsData,
} from './appSettingsAPI';

// type ToolSettings = {
//   blocks: BlockType[];
//   lists: ListType[];
// };

export const emptySettings: IAppSettings = {
  id: 0,
  tools: {},
  rights: {},
  maxmbs: {
    // img: 10_000_000,
    // doc: 20_000_000,
    // xls: 20_000_000,
    // ppt: 50_000_000,
    // pdf: 50_000_000,
  },
  config: {},
};

export interface IAppSettingsState {
  settings: IAppSettings | undefined; // undefined = loading
  connectionError: number;
  status: 'idle' | 'loading' | 'failed';
  isSettingsInitialized: boolean; // true when initial settings fetch is complete
  errors: any;
}

export type AppSettingsStateProps = {
  settings: IAppSettings;
  connectionError: number;
};

export const initialState: IAppSettingsState = {
  settings: undefined, // undefined means loading
  connectionError: 0,
  status: 'loading',
  isSettingsInitialized: false, // false until initial settings fetch is complete
  errors: '',
};

export const fetchAppSettingsAsync: any = createAsyncThunk(
  'appsettings/fetchAppSettingsAsync',
  async (id: number, thunkAPI) => {
    try {
      const res = { response: await fetchAppSettings() };
      if (res.response.data.length === 0) {
        thunkAPI.dispatch(postAppSettingsAsync({ ...emptySettings }));
      }
      return res;
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const fetchAppSettingsWithRetryAsync: any = createAsyncThunk(
  'appsettings/fetchAppSettingsWithRetryAsync',
  async (_, thunkAPI) => {
    try {
      console.log('⚙️ Fetching application settings');
      const result = await thunkAPI.dispatch(fetchAppSettingsAsync()).unwrap();
      return result;
    } catch (error: any) {
      console.error('Error fetching app settings:', error);
      const isNetworkError = !error.response && error.code;
      const isServerError = error.response?.status === 500;
      if (isNetworkError) {
        console.log('Network error');
        return thunkAPI.rejectWithValue({ response: error });
      } else if (isServerError) {
        console.log('Server error - removing token and retrying...');
        // Remove potentially invalid token
        const Cookies = (await import('js-cookie')).default;
        Cookies.remove('token');
        try {
          const retryResult = await thunkAPI
            .dispatch(fetchAppSettingsAsync())
            .unwrap();
          return retryResult;
        } catch (retryError: any) {
          console.error('Retry failed:', retryError);
          return thunkAPI.rejectWithValue({ response: retryError });
        }
      } else {
        console.log('Unknown error');
        return thunkAPI.rejectWithValue({ response: error });
      }
    }
  },
);

export const postAppSettingsAsync = createAsyncThunk(
  'appsettings/postAppSettingsData',
  async (data: IAppSettings, thunkAPI) => {
    try {
      return {
        response: await postAppSettingsData(data),
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const patchAppSettingsAsync = createAsyncThunk(
  'appsettings/patchAppSettingsData',
  async (
    {
      data,
    }: {
      data: IAppSettings;
    },
    thunkAPI,
  ) => {
    try {
      return {
        response: await patchAppSettingsData(data),
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const appSettingsSlice = createSlice({
  name: 'appSettings',
  initialState,
  reducers: {
    setConnectionError: (state, action) => {
      state.connectionError = action.payload;
    },
    setSettingsInitialized: (state, action) => {
      state.isSettingsInitialized = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchAppSettingsAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchAppSettingsAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        if (action.payload.response.data?.length > 0) {
          state.settings = action.payload.response.data[0] as IAppSettings;
          if (!state.settings.maxmbs) state.settings.maxmbs = {};
        }
        state.isSettingsInitialized = true;
      })
      .addCase(fetchAppSettingsAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.errors = action.payload;
        state.isSettingsInitialized = true; // Allow app to continue even if settings fail
      })
      .addCase(postAppSettingsAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(postAppSettingsAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.settings = action.meta.arg;
        state.isSettingsInitialized = true;
      })
      .addCase(postAppSettingsAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.errors = action.payload;
      })
      .addCase(patchAppSettingsAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(patchAppSettingsAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.settings = action.meta.arg.data;
      })
      .addCase(patchAppSettingsAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.errors = action.payload;
      })
      .addCase(fetchAppSettingsWithRetryAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchAppSettingsWithRetryAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.errors = action.payload;
      });
  },
});

export const appSettingsActions = appSettingsSlice.actions;
export const { setConnectionError, setSettingsInitialized } =
  appSettingsSlice.actions;

export default appSettingsSlice.reducer;
